import React, { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import styles from '@common/inputFields/inputFields.module.scss';
import {
  useDuplicationMobileCheckWhenLogin,
  useVerificationUserSmsCheck,
  useVerificationUserSmsSend,
} from '@repository/verification/useMobile';
import CountryCode from '@components/loginModal/find/form/mobile/CountryCode';
import { cellphoneValidator } from '@utils/validators/validators';
import { ERROR_TEXT } from '@utils/validators/text';
import useTimer from '@utils/hooks/useTimer';
import classnames from 'classnames/bind';

type CertStateType = 'ING' | 'END' | 'SUCCEESS' | null;
const cx = classnames.bind(styles);

interface MobileWrapProps {
  name: string;
  visible?: boolean;
  setIsSuccessMobileAuth: (isSuccessMobileAuth: boolean) => void;
  resetValue?: string | null;
  mobileInputDisabled?: boolean;
}

const MobileWrap = ({
  name,
  visible,
  setIsSuccessMobileAuth,
  resetValue,
  mobileInputDisabled = false,
}: MobileWrapProps) => {
  const {
    setValue,
    register,
    getValues,
    setError,
    watch,
    formState: { isSubmitted, errors },
  } = useFormContext();
  // 휴대폰 번호 초기값 filed dirty 상태 체크용
  const [initialMobile, setInitialMobile] = useState<string | null | undefined>(null);
  // 휴대폰 인증 모달
  const [isShowMobileChangeArea, setIsShowMobileChangeArea] = useState<boolean>(false);
  // 변경할 휴대폰 번호 중복
  const [isDuplicate, setIsDuplicate] = useState(false);
  // Mobile 인증 진행 상태 값
  const [certState, setCertState] = useState<CertStateType>(null);

  const { mutateAsync: smsCheck } = useVerificationUserSmsCheck();
  const { mutateAsync: duplicationCheck } = useDuplicationMobileCheckWhenLogin();
  const { mutate: smsSend } = useVerificationUserSmsSend();

  const { field: mobileField, fieldState, formState } = useController({ name });
  const { getRemainingTimeFormat, onStart, onStop } = useTimer({
    time: 3 * 60,
    start: false,
    onCountUp: () => setCertState('END'),
  });
  const removeHypenMobileNum = mobileField.value?.replace(/[^0-9]/g, '') || '';
  const isCorrectMobile = cellphoneValidator(removeHypenMobileNum) === true;
  const countryCode = watch('countryCode');
  const token = watch('token');
  const identified = getValues('identified');
  const userIdentificationType = getValues('userIdentificationType');

  const tokenErrorMessage = errors.token?.message ? `${errors.token?.message}` : '';

  const isDisabled =
    initialMobile === removeHypenMobileNum || !isCorrectMobile || certState === 'ING' || certState === 'SUCCEESS';

  const getIsDisabled = () => {
    if (identified && userIdentificationType === 'SMS') return true;
    return isShowMobileChangeArea || !!mobileInputDisabled;
  };

  const onInit = () => {
    setInitialMobile(resetValue);
    setCertState(null);
    setIsShowMobileChangeArea(false);
    setIsDuplicate(false);
    setIsSuccessMobileAuth(false);
  };

  const onBlur = () => {
    if (!isCorrectMobile) mobileField.onChange(resetValue);
  };

  const onClickCheckCert = async () => {
    const { success } = await smsCheck({ mobile: removeHypenMobileNum, token });
    if (!success) setError('token', { message: ERROR_TEXT.MOBILE_CERT });
    if (success) {
      setIsShowMobileChangeArea(false);
      setCertState('SUCCEESS');
      onStop();
    }

    setIsSuccessMobileAuth(success);
    return;
  };

  const onClickRequestCert = async () => {
    const { isDuplicate } = await duplicationCheck({ mobile: removeHypenMobileNum, countryCode });

    if (!isDuplicate) {
      setCertState('ING');
      smsSend({ mobile: removeHypenMobileNum, countryCode });
      setIsShowMobileChangeArea(true);
      onStart();
      setIsDuplicate(false);
    } else {
      setIsDuplicate(true);
    }
  };

  const onCancleRequestCert = () => {
    onInit();
    onStart();
    mobileField.onChange(resetValue);
  };

  function autoHypenMobile() {
    let convertMobile = '';

    if (removeHypenMobileNum.length < 4) {
      return removeHypenMobileNum;
    } else if (removeHypenMobileNum.length < 7) {
      convertMobile += removeHypenMobileNum.substring(0, 3);
      convertMobile += '-';
      convertMobile += removeHypenMobileNum.substring(3);
    } else if (removeHypenMobileNum.length < 11) {
      convertMobile += removeHypenMobileNum.substring(0, 3);
      convertMobile += '-';
      convertMobile += removeHypenMobileNum.substring(3, 6);
      convertMobile += '-';
      convertMobile += removeHypenMobileNum.substring(6);
    } else {
      convertMobile += removeHypenMobileNum.substring(0, 3);
      convertMobile += '-';
      convertMobile += removeHypenMobileNum.substring(3, 7);
      convertMobile += '-';
      convertMobile += removeHypenMobileNum.substring(7);
    }

    return convertMobile;
  }

  useEffect(() => {
    if (visible) onInit();
  }, [visible, isSubmitted]);

  useEffect(() => {
    if (mobileField.value !== '해외거주자입니다.') mobileField.onChange(autoHypenMobile());
    onInit();
  }, [mobileField.value]);

  useEffect(() => {
    setValue('token', token?.replace(/[^0-9]/g, '').slice(0, 6));
  }, [token]);

  useEffect(() => {
    if (certState === 'END') {
      onInit();
      mobileField.onChange(resetValue);
    }
  }, [certState]);

  return (
    <div className={cx('mobileArea')}>
      <div className={cx('mobileInner')}>
        <div className={cx('mobileWrap')}>
          <CountryCode cx={classnames.bind(styles)} disabled={getIsDisabled()} isLabelPlaceholder={false} />
          <input
            type="text"
            className={cx('input', 'mobile', { error: isDuplicate }, { disabled: getIsDisabled() })}
            placeholder="휴대폰 번호를 입력해주세요."
            autoComplete="no"
            {...mobileField}
            maxLength={13}
            disabled={getIsDisabled()}
            onBlur={onBlur}
          />
          <div className={cx('mobileBtnArea')}>
            {certState === 'ING' ? (
              <button className={cx('btn')} type="button" onClick={onCancleRequestCert}>
                취소
              </button>
            ) : (
              <button className={cx('btn')} type="button" disabled={isDisabled} onClick={onClickRequestCert}>
                변경
              </button>
            )}
          </div>
        </div>
        {isDuplicate && <div className={cx('errorMessage')}>중복된 휴대폰 번호입니다.</div>}
        {certState === 'SUCCEESS' && <div className={cx('changeMessage')}>정상적으로 변경되었습니다.</div>}
      </div>

      {/* [D] 인증번호 전송 시 노출되는 영역 */}
      {isShowMobileChangeArea && (
        <div className={cx('mobileInner')}>
          <div className={cx('mobileWrap')}>
            <input
              {...register('token')}
              type="text"
              name={'token'}
              className={cx('input', { error: errors.token })}
              placeholder="인증 번호를 입력해주세요."
              autoComplete="no"
            />
            <div className={cx('mobileBtnArea')}>
              <span className={cx('timer')}>
                <span>{getRemainingTimeFormat('mm:ss')}</span>
              </span>
              <button
                className={cx('btn')}
                type="button"
                disabled={token.length !== 6 || certState === 'END'}
                onClick={onClickCheckCert}
              >
                확인
              </button>
            </div>
          </div>
          {tokenErrorMessage && <div className={cx('errorMessage')}>{tokenErrorMessage}</div>}
        </div>
      )}
    </div>
  );
};

export default MobileWrap;
